import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box, Link } from "@mui/material";
import { markdownProseSx } from "../../chat/markdownProseSx";
import { parseCitations } from "../../../features/narrative-citations/parseCitations";
import { renderCitedSegments } from "../../../features/narrative-citations/renderCitedSegments";
import type { CitationGroup } from "../../../features/narrative-citations/types";

interface CitedMarkdownProps {
  content: string;
  /** Invoked when a citation anchor is activated (seek the evidence player). */
  onCite?: (group: CitationGroup) => void;
}

/**
 * Walk a rendered element's children; for each plain-string child, decode and
 * hide inline citation tokens, turning the cited phrase into a clickable anchor.
 * Element children (bold/links/nested lists) are left to react-markdown's own
 * component dispatch, which re-enters these overrides. Citation markers only
 * appear in plain prose (the recipe forbids them inside markdown emphasis/code),
 * so transforming string children is sufficient.
 */
function decorateChildren(
  children: React.ReactNode,
  onCite: ((group: CitationGroup) => void) | undefined,
  keyBase: string,
): React.ReactNode {
  return React.Children.map(children, (child, i) => {
    if (typeof child !== "string") return child;
    const { cleanText, groups } = parseCitations(child);
    if (groups.length === 0) return cleanText;
    return (
      <React.Fragment key={`${keyBase}-${i}`}>
        {renderCitedSegments(cleanText, groups, onCite, `${keyBase}-${i}`)}
      </React.Fragment>
    );
  });
}

/**
 * Render markdown (same prose styling as {@link MarkdownContent}) while turning
 * inline citation tokens into clickable, tooltip-backed anchors. Used by the
 * plan-review Outline so the briefing carries the same citations as the
 * narrative. Tokens are review-only — they are stripped before RMS export and
 * the printed report.
 */
export const CitedMarkdown: React.FC<CitedMarkdownProps> = ({
  content,
  onCite,
}) => {
  // Unescape literal \n that may arrive from JSON (matches MarkdownContent).
  const processedContent = content.replace(/\\n/g, "\n");

  const components = React.useMemo<Components>(() => {
    const decorate =
      (Tag: string, key: string) =>
      ({
        node: _node,
        children,
        ...rest
      }: {
        node?: unknown;
        children?: React.ReactNode;
        [prop: string]: unknown;
      }) =>
        React.createElement(
          Tag,
          rest,
          decorateChildren(children, onCite, key),
        );
    const map = {
      p: decorate("p", "p"),
      li: decorate("li", "li"),
      td: decorate("td", "td"),
      th: decorate("th", "th"),
      h1: decorate("h1", "h1"),
      h2: decorate("h2", "h2"),
      h3: decorate("h3", "h3"),
      h4: decorate("h4", "h4"),
      h5: decorate("h5", "h5"),
      h6: decorate("h6", "h6"),
      em: decorate("em", "em"),
      strong: decorate("strong", "strong"),
      blockquote: decorate("blockquote", "bq"),
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <Link href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </Link>
      ),
    };
    return map as unknown as Components;
  }, [onCite]);

  return (
    <Box sx={markdownProseSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};
